<template>
    <v-container flat style="max-width: 1000px">
        <v-window v-model="step" touchless>
            <v-window-item :value="0">
                <UploadDocuments
                    :showFirstPrevious="false"
                    :documentType="'PRODUCTS'"
                    :inExtensions="['csv', 'tsv', 'txt', 'xlsx', 'xls']"
                    :multiple="false"
                    :freeTag="false"
                    :gDrive="true"
                    @fileInput="onFileChanged"
                    @fileDeleted="onFileDeleted"
                    @testFileInput="onTestFileChanged"
                />
            </v-window-item>
            <v-window-item :value="1">
                <ColumnCorrespondence
                    ref="columnComponent"
                    v-if="step > 0 && existingFile"
                    :result="result"
                    :extension="extension"
                    :actions="actions"
                    :ruleManager="ruleManager"
                    :formatColumns="formatColumns"
                    @columns-chosen="onChosenColumns($event)"
                    @back="step = 0"
                />
            </v-window-item>
            <v-window-item :value="2">
                <v-data-table
                    :headers="importedHeaders"
                    :items="importedItems"
                    class="table"
                    fixed-header
                    density="compact"
                    :loading="verifLoading"
                    :items-per-page-options="[10, 25, 50]"
                >
                    <template
                        v-slot:item="{ item, index }"
                        v-if="documentType === 'INVOICE_PRODUCTS'"
                    >
                        <tr
                            :style="
                                'background-color:' + item.colorAlert + '; pointer-events: none'
                            "
                        >
                            <td class="text-center">{{ item.reference }}</td>
                            <td class="text-center">{{ item.title }}</td>
                            <td class="text-center">{{ item.unitPrice }}</td>
                            <td class="text-center">{{ item.vatRate }}</td>

                            <td v-if="salesAccountableAccounts.length > 1" class="align-center">
                                <v-select-filled
                                    hide-details
                                    density="compact"
                                    style="pointer-events: auto"
                                    v-model="item.accountableAccountId"
                                    :items="salesAccountableAccounts"
                                    item-title="description"
                                    item-value="id"
                                    class="py-1"
                                />
                            </td>
                            <td
                                v-else-if="salesAccountableAccounts.length === 1"
                                class="text-center"
                            >
                                {{ salesAccountableAccounts[0].description }}
                            </td>
                            <td v-else class="text-center" />

                            <td v-if="item.status === 'EXISTING_PRODUCT'" class="text-center">
                                Le produit sera mis à jour
                            </td>
                            <td v-else-if="item.status === 'NEW_PRODUCT'" class="text-center">
                                <v-icon icon="$fa-check" color="green" />
                            </td>
                            <td v-else class="text-center">
                                <v-progress-circular indeterminate color="primary" />
                            </td>
                        </tr>
                    </template>
                    <template
                        v-slot:item="{ item, index }"
                        v-else-if="documentType === 'STOCK_PRODUCTS'"
                    >
                        <tr
                            :style="
                                'background-color:' + item.colorAlert + '; pointer-events: none'
                            "
                        >
                            <td class="text-center">{{ item.reference }}</td>
                            <td class="text-center">{{ item.title }}</td>
                            <td class="text-center">{{ item.unitPrice }}</td>
                            <td class="align-center">
                                <v-select-filled
                                    hide-details
                                    outlined
                                    density="compact"
                                    style="pointer-events: auto"
                                    v-model="item.type"
                                    :items="[
                                        { type: 'PRODUCT', description: 'Produit' },
                                        { type: 'EXPENSE', description: 'Achat' },
                                    ]"
                                    item-title="description"
                                    item-value="type"
                                />
                            </td>

                            <td v-if="item.type === 'PRODUCT'" class="align-center">
                                <v-select-filled
                                    hide-details
                                    density="compact"
                                    style="pointer-events: auto"
                                    v-model="item.accountableAccountId"
                                    :items="salesAccountableAccounts"
                                    item-title="description"
                                    item-value="id"
                                    class="py-1"
                                />
                            </td>
                            <td v-else-if="item.type === 'EXPENSE'" class="align-center">
                                <v-select-filled
                                    hide-details
                                    density="compact"
                                    style="pointer-events: auto"
                                    v-model="item.accountableAccountId"
                                    :items="purchaseAccountableAccounts"
                                    item-title="description"
                                    item-value="id"
                                    class="py-1"
                                />
                            </td>
                            <td v-else class="text-center" />

                            <td v-if="item.status === 'EXISTING_PRODUCT'" class="text-center">
                                Le produit sera mis à jour
                            </td>
                            <td v-else-if="item.status === 'NEW_PRODUCT'" class="text-center">
                                <v-icon icon="$fa-check" color="green" />
                            </td>
                            <td v-else class="text-center">
                                <v-progress-circular indeterminate color="primary" />
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-window-item>
        </v-window>
    </v-container>
</template>

<script lang="ts">
import { Apollo } from "../../decorators";
import { Component, Prop, Watch, toNative } from "vue-facing-decorator";

import ColumnCorrespondence from "./ColumnCorrespondence.vue";
import { ImportDocumentBase } from "./ImportDocumentBase";
import OssDataTable from "../ossComponents/ossDataTable2.vue";

import { DialogAction } from "../../services/dialogAction";

import { filterAccountableAccounts } from "../../../../backend/src/common/accountableAccount/accountableAccountServices";

import REVENUE_ACCOUNTABLE_ACCOUNTS_QUERY from "../../graphql/accountableAccount/RevenueAccountableAccounts.gql";

import INVOICE_PRODUCTS_VERIF from "../../graphql/invoice/Products.gql";
import STOCK_PRODUCTS_VERIF from "../../graphql/stock/StockProductsVerif.gql";

import STOCK_PRODUCTS_IMPORT from "../../graphql/stock/StockProductsImport.gql";
import PRODUCTS_IMPORT from "../../graphql/invoice/ProductsImport.gql";
import UploadDocuments from "../document/UploadDocuments.vue";

@Component({
    components: {
        UploadDocuments,
        ColumnCorrespondence,
        OssDataTable,
    },
    emits: ["documentImported", "width"],
})
export class InvoiceProductImport extends ImportDocumentBase {
    @Prop({ default: null }) actions: { action: DialogAction } | null;
    @Prop({ default: "" }) selectedCompanyId: string;
    @Prop({ default: "" }) accountingFirmId: string;

    step = 0;

    verifLoading: boolean = false;
    importLoading: boolean = false;

    beforeMount() {
        this.setActionsButton();
    }

    @Apollo({
        query() {
            return REVENUE_ACCOUNTABLE_ACCOUNTS_QUERY;
        },
        variables() {
            return {
                companyId: this.selectedCompanyId,
                vatRate0: false,
                withOtherProducts: true,
            };
        },
        // debounce: 500,
        skip() {
            return !this.selectedCompanyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            this.loadingDocument = loading;
            if (!loading) {
                if (data && data.companySalesAccounts && data.companySalesAccounts.salesAccounts) {
                    this.salesAccountableAccounts = data.companySalesAccounts.salesAccounts;
                }
            }
        },
    })
    salesAccountableAccounts: Array<any> = [];

    // @Apollo({
    //     query() {
    //         return ACCOUNTABLE_ACCOUNTS_STARTS_WITH
    //     },
    //     variables(){
    //         return { range: [
    //             {name_starts_with: "601"},
    //             {name_starts_with: "602"},
    //             {name_starts_with: "603"},
    //             {name_starts_with: "607"},
    //             {name_starts_with: "713"},
    //         ] }
    //     },
    //     skip(){
    //         return !this.selectedCompanyId || this.documentType !== 'STOCK_PRODUCTS'
    //     },
    //     result({data, loading}: any) {
    //         if (!loading) {
    //             if (data && data.accountableAccounts) {
    //                 const filterPurchaseAAs = ["601000","601005","601010","601020","601100","601130","601700","602100","602200","602100","602200","602210","602220","602230","602240","602250","602600","602610","602650","602670"]
    //                 this.purchaseAccountableAccounts = data.accountableAccounts.filter(aa => filterPurchaseAAs.indexOf(aa.name) !== -1)
    //             } else {
    //                 console.log("AccountableAccountVue data pb");
    //                 this.expenseAccountableAccounts.splice(0, this.expenseAccountableAccounts.length);
    //             }
    //         }
    //     }
    // })

    get purchaseAccountableAccounts() {
        const includeRootAAs = ["601", "602", "603", "607", "713"];
        return filterAccountableAccounts(
            this.$store.state.accountableAccountTable,
            includeRootAAs,
            [],
        );
    }

    async onChosenColumns(data) {
        const { importedItems, importedHeaders } = data;
        this.importedItems = importedItems;
        this.importedHeaders = importedHeaders;
        const result = await this.verifyInvoiceProducts();

        if (result.length) {
            this.verifLoading = false;
            this.step++;
        }
    }

    asignAccountToProduct() {
        for (const item of this.importedItems) {
            if (item.type === "PRODUCT") {
                if (this.salesAccountableAccounts.length === 1) {
                    item.accountableAccountId = this.salesAccountableAccounts[0].id;
                } else {
                    if (!item.accountableAccountId) {
                        item.accountableAccountId = "";
                    }
                }
            }
        }
    }

    async verifyInvoiceProducts() {
        // debugger
        this.verifLoading = true;
        let result;
        switch (this.documentType) {
            case "INVOICE_PRODUCTS":
                if (this.selectedCompanyId && this.importedItems?.length) {
                    const query = await this.$apollo.query({
                        query: INVOICE_PRODUCTS_VERIF,
                        variables: {
                            invoicesProducts: this.importedItems,
                            selectedCompanyId: this.selectedCompanyId,
                        },
                    });
                    result = query.data.invoicesProducts.invoiceProducts || [];
                }
                break;
            case "STOCK_PRODUCTS":
                if (this.selectedCompanyId && this.importedItems?.length) {
                    const query = await this.$apollo.query({
                        query: STOCK_PRODUCTS_VERIF,
                        variables: {
                            stockProducts: this.importedItems,
                            selectedCompanyId: this.selectedCompanyId,
                        },
                    });
                    result = query.data.stocksProducts.stockProducts || [];
                }
                break;
            default:
                result = [];
                break;
        }
        if (result?.length) {
            this.importedItems = JSON.parse(JSON.stringify(result));
            this.asignAccountToProduct();
        }
        return result;
    }

    async valid() {
        // debugger
        this.importLoading = true;
        switch (this.documentType) {
            case "INVOICE_PRODUCTS":
                if (this.selectedCompanyId && this.importedItems?.length) {
                    const result = await this.$apollo.mutate({
                        mutation: PRODUCTS_IMPORT,
                        variables: {
                            invoicesProducts: this.importedItems,
                            selectedCompanyId: this.selectedCompanyId,
                        },
                    });
                    if (result?.data?.invoicesProductsImport?.invoiceProducts) {
                        this.importLoading = false;
                        this.$emit("documentImported");
                    } else {
                        debugger;
                    }
                }
                break;
            case "STOCK_PRODUCTS":
                if (this.selectedCompanyId && this.importedItems?.length) {
                    const result = await this.$apollo.mutate({
                        mutation: STOCK_PRODUCTS_IMPORT,
                        variables: {
                            stockProducts: this.importedItems,
                            selectedCompanyId: this.selectedCompanyId,
                        },
                    });
                    if (result?.data?.stocksProductsImport?.stockProducts) {
                        this.importLoading = false;
                        this.$emit("documentImported");
                    } else {
                        debugger;
                    }
                }
                break;
            default:
                break;
        }
    }

    importDisabled() {
        if (!this.importedItems.length) {
            return true;
        }
        if (this.documentType === "STOCK_PRODUCTS") {
            if (this.importedItems.some((p) => !!!p.type)) {
                return true;
            }
            if (this.importedItems.some((p) => !!!p.accountableAccountId)) {
                return true;
            }
        }
        return false;
    }

    @Watch("step")
    setActionsButton() {
        if (this.actions && this.actions.action) {
            if (this.step === 0) {
                this.actions.action.display = true;
                this.actions.action.title = "Suivant";
                this.actions.action.disabled = () => {
                    return !this.existingFile;
                };
                this.actions.action.function = () => {
                    this.step++;
                };
            } else if (this.step === 1) {
                this.actions.action.display = true;
                this.actions.action.title = "Suivant";
                if (this.$refs.columnComponent) {
                    //@ts-ignore
                    this.$refs.columnComponent.setActionButton();
                }
                //@ts-ignore
                this.actions.action.loading = () => {
                    return this.verifLoading;
                };
            } else if (this.step === 2) {
                this.actions.action.display = true;
                this.actions.action.title = "Importer";
                this.actions.action.disabled = () => {
                    return this.importDisabled();
                };
                this.actions.action.function = async () => {
                    await this.valid();
                };
                this.actions.action.previous = () => {
                    this.step--;
                };
                //@ts-ignore
                this.actions.action.loading = () => {
                    return this.importLoading;
                };
            }
        }
        this.$emit("width");
    }
}

export default toNative(InvoiceProductImport);
</script>
